/**
 * Coalesces paired CREATE / DELETE GraphQL mutations for the same annotation id.
 *
 * Problem: when a user spams Ctrl+Z / Ctrl+Shift+Z across the point-removal
 * boundary that deletes an area annotation, the engine dispatches a rapid
 * sequence of `measurement_delete` and `measurement_finish` events for the
 * same identifier. Each event triggers a GraphQL mutation; those mutations
 * can race against each other server-side.
 *
 * Fix: defer each mutation by a short window. If the opposite mutation for
 * the same id arrives within that window, both cancel — the net server state
 * is unchanged, so we skip both round-trips. Optimistic cache writes still
 * happen synchronously at the call site, so the UI stays responsive.
 */

type MutationKind = 'create' | 'delete';

interface PendingMutation {
  kind: MutationKind;
  fire: () => unknown;
  timer: ReturnType<typeof setTimeout>;
}

const WINDOW_MS = 500;

const pending = new Map<string, PendingMutation>();
/** Ids whose mutation has been sent but whose response hasn't landed yet. */
const inFlight = new Map<string, MutationKind>();
/**
 * State mutations tracked from OUTSIDE the coalescer (group creates, bulk
 * deletes): their optimistic entries need the same clobber protection while
 * the mutation is in flight.
 */
const externalPending = new Map<symbol, { created: Set<string>; deleted: Set<string>; moved: Map<string, string | null> }>();
const settleListeners = new Set<() => void>();

function notifySettled(): void {
  for (const listener of [...settleListeners]) listener();
}

/**
 * True while the annotation's create/delete is deferred in the window or its
 * mutation response hasn't landed yet. During this span the annotation exists
 * only as an optimistic cache entry: any OTHER mutation returning full project
 * state would clobber it, so dependent mutations (e.g. room folder
 * auto-creation) must wait this out.
 */
export function isAnnotationMutationPending(identifier: string): boolean {
  return pending.has(identifier) || inFlight.has(identifier);
}

/**
 * Register a state mutation fired outside the coalescer (e.g. group create,
 * bulk delete, group move/ungroup) whose optimistic cache entries must survive
 * concurrent full-state responses until `settled` resolves. Never rethrows.
 *
 * `moved` entries protect an annotation's optimistic groupIdentifier at READ
 * time (see the Annotation.groupIdentifier field policy in apollo/cache.ts) —
 * `groupIdentifier: null` means "moved to root".
 */
export function trackPendingStateMutation(
  ids: { created?: readonly string[]; deleted?: readonly string[]; moved?: readonly { identifier: string; groupIdentifier: string | null }[] },
  settled: Promise<unknown>,
): void {
  const key = Symbol('pending-state-mutation');
  externalPending.set(key, {
    created: new Set(ids.created),
    deleted: new Set(ids.deleted),
    moved: new Map((ids.moved ?? []).map((m) => [m.identifier, m.groupIdentifier])),
  });
  void settled
    .catch(() => undefined)
    .finally(() => {
      externalPending.delete(key);
      notifySettled();
    });
}

/**
 * The optimistic groupIdentifier of an in-flight move, or `undefined` when no
 * move is pending for the id (`null` = pending move to root). A concurrent
 * full-state response may rewrite the stored field to the pre-move value; the
 * cache read policy consults this so reads keep reflecting the move until its
 * own response lands. Last-registered move wins.
 */
export function getPendingMovedGroupId(identifier: string): string | null | undefined {
  let result: string | null | undefined;
  for (const { moved } of externalPending.values()) {
    if (moved.has(identifier)) result = moved.get(identifier) ?? null;
  }
  return result;
}

/**
 * Identifiers created optimistically whose confirming mutation hasn't landed
 * yet — a full-state response missing them reflects a server that hasn't
 * processed the create, NOT a deletion. Consumed by the Project.state cache
 * merge policy.
 */
export function getPendingCreatedIds(): Set<string> {
  const result = new Set<string>();
  for (const [id, entry] of pending) if (entry.kind === 'create') result.add(id);
  for (const [id, kind] of inFlight) if (kind === 'create') result.add(id);
  for (const { created } of externalPending.values()) for (const id of created) result.add(id);
  return result;
}

/**
 * Identifiers deleted optimistically whose confirming mutation hasn't landed
 * yet — a full-state response still containing them must not resurrect them.
 */
export function getPendingDeletedIds(): Set<string> {
  const result = new Set<string>();
  for (const [id, entry] of pending) if (entry.kind === 'delete') result.add(id);
  for (const [id, kind] of inFlight) if (kind === 'delete') result.add(id);
  for (const { deleted } of externalPending.values()) for (const id of deleted) result.add(id);
  return result;
}

/**
 * Notifies when any deferred mutation settles (response landed, or a
 * create/delete pair canceled). Returns an unsubscribe function.
 */
export function onAnnotationMutationSettled(listener: () => void): () => void {
  settleListeners.add(listener);
  return () => {
    settleListeners.delete(listener);
  };
}

function schedule(identifier: string, kind: MutationKind, fire: () => unknown): void {
  const existing = pending.get(identifier);

  if (existing) {
    clearTimeout(existing.timer);
    pending.delete(identifier);
    if (existing.kind !== kind) {
      // Opposite op arrived inside the window → the pair is a no-op on the
      // server. Drop both without firing either mutation.
      notifySettled();
      return;
    }
    // Same kind twice is unexpected (same identifier cannot be created or
    // deleted twice in a row), but be defensive: replace the pending call.
  }

  const timer = setTimeout(() => {
    pending.delete(identifier);
    inFlight.set(identifier, kind);
    void Promise.resolve()
      .then(fire)
      .catch(() => undefined)
      .finally(() => {
        inFlight.delete(identifier);
        notifySettled();
      });
  }, WINDOW_MS);

  pending.set(identifier, { kind, fire, timer });
}

export function scheduleCreateMutation(identifier: string, fire: () => unknown): void {
  schedule(identifier, 'create', fire);
}

export function scheduleDeleteMutation(identifier: string, fire: () => unknown): void {
  schedule(identifier, 'delete', fire);
}
